import { useState, useCallback } from "react";
import { INITIAL_QUICK_DATA, INITIAL_DETAILS_DATA, EMPTY_RESTAURANT } from "../constants";
import { validateQuickCapture, validateDetails } from "../validation";
import LeadService from "../../../api/lead";

// Screens: 'capture' -> 'details' -> 'success'
export default function useLeadForm() {
  const [screen, setScreen] = useState("capture");
  const [quickData, setQuickData] = useState(INITIAL_QUICK_DATA);
  const [detailsData, setDetailsData] = useState(INITIAL_DETAILS_DATA);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [leadId, setLeadId] = useState(null);

  const updateQuickField = useCallback((field, value) => {
    setQuickData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const updateDetailsField = useCallback((field, value) => {
    setDetailsData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleQuickSubmit = useCallback(async () => {
    const validationErrors = validateQuickCapture(quickData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const payload = {
        full_name: quickData.full_name.trim(),
        whatsapp: quickData.whatsapp.replace(/\D/g, ""),
        restaurants: [{ name: quickData.restaurant_name.trim() }],
      };
      const response = await LeadService.submitLead(payload);
      const newLeadId = response.data.lead_id;
      setLeadId(newLeadId);

      // Pre-populate the first restaurant in details
      setDetailsData((prev) => ({
        ...prev,
        restaurants: [{ ...EMPTY_RESTAURANT, name: quickData.restaurant_name.trim() }],
      }));

      setErrors({});
      setScreen("details");
    } catch (err) {
      const data = err?.response?.data;
      if (data) {
        const backendErrors = {};
        Object.entries(data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value;
        });
        if (Object.keys(backendErrors).length > 0) {
          setErrors(backendErrors);
          setSubmitError(
            backendErrors.whatsapp ||
            backendErrors.non_field_errors ||
            "Verifique os campos e tente novamente."
          );
        } else {
          setSubmitError("Erro ao enviar cadastro. Tente novamente.");
        }
      } else {
        setSubmitError("Erro de conexão. Verifique sua internet e tente novamente.");
      }
    } finally {
      setSubmitting(false);
    }
  }, [quickData]);

  const handleDetailsSubmit = useCallback(async () => {
    const validationErrors = validateDetails(detailsData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const payload = {};

      // Only send non-empty fields
      if (detailsData.email) payload.email = detailsData.email;
      if (detailsData.cpf_cnpj) payload.cpf_cnpj = detailsData.cpf_cnpj.replace(/\D/g, "");
      if (detailsData.business_name) payload.business_name = detailsData.business_name;
      if (detailsData.city) payload.city = detailsData.city;
      if (detailsData.uf) payload.uf = detailsData.uf;
      if (detailsData.address_cep) payload.address_cep = detailsData.address_cep.replace(/\D/g, "");
      if (detailsData.address_street) payload.address_street = detailsData.address_street;
      if (detailsData.address_number) payload.address_number = detailsData.address_number;
      if (detailsData.address_complement) payload.address_complement = detailsData.address_complement;
      if (detailsData.address_neighborhood) payload.address_neighborhood = detailsData.address_neighborhood;
      if (detailsData.address_city) payload.address_city = detailsData.address_city;
      if (detailsData.address_uf) payload.address_uf = detailsData.address_uf;
      if (detailsData.how_heard) payload.how_heard = detailsData.how_heard;
      if (detailsData.message) payload.message = detailsData.message;
      if (detailsData.lgpd_consent) payload.lgpd_consent = true;

      // Always send restaurants (may have been updated with integrations)
      if (detailsData.restaurants && detailsData.restaurants.length > 0) {
        payload.restaurants = detailsData.restaurants.map((r) => ({
          name: r.name.trim(),
          ifood_link_or_id: r.ifood_link_or_id || "",
          uses_delivery_direto: !!r.uses_delivery_direto,
          uses_neemo: !!r.uses_neemo,
        }));
      }

      if (Object.keys(payload).length > 0) {
        await LeadService.completeLead(leadId, payload);
      }

      setErrors({});
      setScreen("success");
    } catch (err) {
      const data = err?.response?.data;
      if (data) {
        const backendErrors = {};
        Object.entries(data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value;
        });
        if (Object.keys(backendErrors).length > 0) {
          setErrors(backendErrors);
          setSubmitError(
            backendErrors.email ||
            backendErrors.non_field_errors ||
            "Verifique os campos e tente novamente."
          );
        } else {
          setSubmitError("Erro ao salvar dados. Tente novamente.");
        }
      } else {
        setSubmitError("Erro de conexão. Verifique sua internet e tente novamente.");
      }
    } finally {
      setSubmitting(false);
    }
  }, [detailsData, leadId]);

  const handleSkip = useCallback(() => {
    setScreen("success");
  }, []);

  return {
    screen,
    quickData,
    detailsData,
    errors,
    submitting,
    submitError,
    updateQuickField,
    updateDetailsField,
    handleQuickSubmit,
    handleDetailsSubmit,
    handleSkip,
  };
}
